import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Check, Mail } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useChangeEmail, useCheckRegistration, useCoRegLogin } from "@/api/mutations/auth-flows";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/api-client";

export const Route = createFileRoute("/_public/register/after/$customer/$token")({
	component: CoRegistrationPage,
});

type Phase = "checking" | "waiting" | "success" | "error";

const POLL_INITIAL_INTERVAL = 2000;
const POLL_MAX_INTERVAL = 30000;
const POLL_TIMEOUT = 5 * 60 * 1000; // 5 Minuten

function CoRegistrationPage() {
	const { customer, token } = Route.useParams();
	const navigate = useNavigate();

	const [phase, setPhase] = useState<Phase>("checking");
	const [newEmail, setNewEmail] = useState("");
	const [emailChanged, setEmailChanged] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const hasTriggered = useRef(false);
	const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pollStartRef = useRef<number>(0);
	const currentIntervalRef = useRef(POLL_INITIAL_INTERVAL);

	const checkMutation = useCheckRegistration();
	const coRegLoginMutation = useCoRegLogin();
	const changeEmailMutation = useChangeEmail();

	// Auto-Login durchführen und weiterleiten
	const performAutoLogin = useCallback(async () => {
		try {
			const result = await coRegLoginMutation.mutateAsync({ customer, token });
			setPhase("success");

			// Co-Reg-Kunden fehlen häufig Geschlecht, Geburtsdatum oder Ort. Mit
			// offenen Steps gehört der Kunde in die Vervollständigung, nicht mit
			// halbem Profil in die Suche.
			if (result.steps) {
				navigate({ to: "/register-steps" });
				return;
			}

			navigate({ to: "/search" });
		} catch (error) {
			const message =
				error instanceof ApiError
					? error.message
					: "Automatischer Login fehlgeschlagen. Bitte melde dich manuell an.";
			setErrorMessage(message);
			setPhase("error");
		}
	}, [customer, token, coRegLoginMutation, navigate]);

	// Polling: Prüfen ob Registrierung abgeschlossen ist
	const pollRegistration = useCallback(async () => {
		const elapsed = Date.now() - pollStartRef.current;
		if (elapsed >= POLL_TIMEOUT) {
			if (pollRef.current) {
				clearTimeout(pollRef.current);
				pollRef.current = null;
			}
			setErrorMessage(
				"Die Registrierung konnte nicht abgeschlossen werden. Bitte versuche es erneut.",
			);
			setPhase("error");
			return;
		}

		try {
			const result = await checkMutation.mutateAsync({ customer, token });
			if (result.registered) {
				if (pollRef.current) {
					clearTimeout(pollRef.current);
					pollRef.current = null;
				}
				await performAutoLogin();
				return;
			}
		} catch {
			// Backend noch nicht bereit, weiter polling
		}

		// Nächsten Poll mit exponential backoff planen
		currentIntervalRef.current = Math.min(currentIntervalRef.current * 2, POLL_MAX_INTERVAL);
		pollRef.current = setTimeout(pollRegistration, currentIntervalRef.current);
	}, [customer, token, checkMutation, performAutoLogin]);

	// Initiale Prüfung on mount
	// biome-ignore lint/correctness/useExhaustiveDependencies: One-time effect mit ref guard
	useEffect(() => {
		if (!hasTriggered.current) {
			hasTriggered.current = true;
			(async () => {
				try {
					const result = await checkMutation.mutateAsync({ customer, token });
					if (result.registered) {
						await performAutoLogin();
					} else {
						setPhase("waiting");
						pollStartRef.current = Date.now();
						currentIntervalRef.current = POLL_INITIAL_INTERVAL;
						pollRef.current = setTimeout(pollRegistration, POLL_INITIAL_INTERVAL);
					}
				} catch (error) {
					const message =
						error instanceof ApiError
							? error.message
							: "Die Registrierungsprüfung ist fehlgeschlagen.";
					setErrorMessage(message);
					setPhase("error");
				}
			})();
		}

		return () => {
			if (pollRef.current) {
				clearTimeout(pollRef.current);
			}
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function handleChangeEmail() {
		if (!newEmail.trim() || emailChanged) return;
		changeEmailMutation.mutate(
			{ email: newEmail.trim(), customer, token },
			{
				onSuccess: () => {
					setEmailChanged(true);
				},
			},
		);
	}

	// --- Render ---

	if (phase === "checking") {
		return (
			<div className="flex flex-col items-center gap-4 py-20">
				<Spinner size="lg" label="Registrierung wird geprüft" />
				<p className="text-text-secondary text-base">Einen Moment bitte...</p>
			</div>
		);
	}

	if (phase === "error") {
		return (
			<div className="max-w-md mx-auto py-20">
				<Card padding="lg">
					<div className="flex flex-col items-center gap-4 text-center">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/20">
							<AlertTriangle size={32} className="text-error" />
						</div>
						<h1 className="text-xl font-bold text-text">Registrierung fehlgeschlagen</h1>
						<p className="text-base text-text-secondary">{errorMessage}</p>
						<Button variant="primary" onClick={() => navigate({ to: "/login" })}>
							Zum Login
						</Button>
					</div>
				</Card>
			</div>
		);
	}

	if (phase === "success") {
		return (
			<div className="flex flex-col items-center gap-4 py-20">
				<div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
					<Check size={32} className="text-success" />
				</div>
				<p className="text-text text-lg font-bold">Willkommen!</p>
				<p className="text-text-secondary text-base">Du wirst weitergeleitet...</p>
			</div>
		);
	}

	// phase === "waiting"
	return (
		<div className="max-w-md mx-auto py-20">
			<Card padding="lg">
				<div className="flex flex-col items-center gap-6 text-center">
					<div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20">
						<Mail size={32} className="text-primary" />
					</div>
					<div>
						<h1 className="text-xl font-bold text-text">Dein Account wird erstellt...</h1>
						<p className="mt-2 text-base text-text-secondary">
							Wir richten deinen Account ein. Dies kann einen Moment dauern. Du wirst automatisch
							weitergeleitet, sobald alles bereit ist.
						</p>
					</div>

					<Spinner size="md" label="Account wird erstellt" />

					{/* E-Mail ändern (einmalig) */}
					<div className="w-full border-t border-border pt-4">
						<p className="text-base text-text-secondary mb-3">
							E-Mail-Adresse ändern? Du kannst deine E-Mail einmalig anpassen.
						</p>
						<div className="flex gap-2">
							<Input
								type="email"
								placeholder="Neue E-Mail-Adresse"
								value={newEmail}
								onChange={(e) => setNewEmail(e.target.value)}
								disabled={emailChanged}
								fullWidth
							/>
							<Button
								variant="outline"
								onClick={handleChangeEmail}
								disabled={emailChanged || !newEmail.trim()}
								loading={changeEmailMutation.isPending}
								className="shrink-0"
							>
								{emailChanged ? "Geändert" : "Ändern"}
							</Button>
						</div>
						{changeEmailMutation.isError && (
							<p className="mt-2 text-base text-error">
								{changeEmailMutation.error instanceof ApiError
									? changeEmailMutation.error.message
									: "E-Mail konnte nicht geändert werden."}
							</p>
						)}
						{emailChanged && (
							<p className="mt-2 text-base text-success">
								E-Mail-Adresse wurde erfolgreich geändert.
							</p>
						)}
					</div>
				</div>
			</Card>
		</div>
	);
}
